import React, { useState } from "react";
import { Header } from "../components/shared";
import { CheckoutFooter } from "../components/checkout";
import { useTranslation } from "../hooks/useTranslation";
import ReactGA from "react-ga4";
import { MEDIA_URL } from '../config/constants';
import { SEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from "../seo";


const Contact = () => {
  const { t } = useTranslation('contact');

  // SEO Schemas
  const schemas = [
    generateLocalBusinessSchema(),
    generateBreadcrumbSchema([
      { name: 'Home', path: '' },
      { name: 'Contact', path: '/contact' },
    ]),
  ];

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: null });

    // Track in Google Analytics
    ReactGA.event({
      category: 'Form',
      action: 'Contact Form Submit',
      label: 'contact_page'
    });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(process.env.REACT_APP_API_KEY && { "X-API-Key": process.env.REACT_APP_API_KEY }),
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      // Success
      setStatus({ submitting: false, submitted: true, error: null });
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
      });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setStatus((prev) => ({ ...prev, submitted: false }));
      }, 5000);
    } catch (error) {
      setStatus({
        submitting: false,
        submitted: false,
        error: error.message,
      });
    }
  };

  return (
    <div className="contact-page">
      <SEO schema={schemas} />
      <Header />
      <div className="contact-main-container">
        <div className="contact-content-container">
          <div className="contact-faq-header">
            <h1 className="contact-page-title">
              {t('page.title')}
            </h1>
            <p className="contact-page-subtitle">
              {t('page.subtitle')}
            </p>
          </div>

          <div className="contact-white-container">
            <div className="contact-info-container">
              <div className="contact-address-container">
                <div className="contact-address-icon-container">
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21.1679 10.0833H30.2118C30.8827 10.0833 31.2182 10.0833 31.4144 10.2243C31.5856 10.3474 31.697 10.5368 31.7215 10.7462C31.7495 10.9863 31.5866 11.2795 31.2608 11.866L29.0564 15.8338C28.9383 16.0465 28.8792 16.1529 28.856 16.2655C28.8355 16.3652 28.8355 16.468 28.856 16.5677C28.8792 16.6803 28.9383 16.7867 29.0564 16.9994L31.2608 20.9671C31.5866 21.5537 31.7495 21.8469 31.7215 22.0869C31.697 22.2963 31.5856 22.4858 31.4144 22.6089C31.2182 22.7499 30.8827 22.7499 30.2118 22.7499H18.8179C17.9778 22.7499 17.5578 22.7499 17.2369 22.5864C16.9546 22.4426 16.7252 22.2131 16.5814 21.9309C16.4179 21.61 16.4179 21.19 16.4179 20.3499V16.4166M10.8762 32.2503L4.54289 6.91699M6.91795 16.4167H18.7679C19.608 16.4167 20.028 16.4167 20.3489 16.2532C20.6311 16.1094 20.8606 15.8799 21.0044 15.5977C21.1679 15.2768 21.1679 14.8567 21.1679 14.0167V6.15C21.1679 5.30992 21.1679 4.88988 21.0044 4.56901C20.8606 4.28677 20.6311 4.0573 20.3489 3.91349C20.028 3.75 19.608 3.75 18.7679 3.75H6.82505C5.77727 3.75 5.25338 3.75 4.89506 3.9671C4.581 4.15738 4.34765 4.45625 4.23923 4.80708C4.11552 5.20736 4.24258 5.71561 4.49672 6.73211L6.91795 16.4167Z" stroke="#009B31" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="contact-address-text-container">
                  <div className="contact-address-title">{t('info.hq.title')}</div>
                  <div className="contact-address-details">{t('info.hq.address')}</div>
                </div>
              </div>

              <div className="contact-phone-container">
                <div className="contact-phone-icon-container">
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21.0745 9C22.5396 9.28585 23.8861 10.0024 24.9416 11.0579C25.9971 12.1134 26.7136 13.4599 26.9995 14.925M21.0745 3C24.1184 3.33815 26.9569 4.70126 29.1239 6.86551C31.2908 9.02976 32.6575 11.8665 32.9995 14.91M15.34 20.7946C13.5376 18.9922 12.1144 16.9543 11.0704 14.7798C10.9806 14.5928 10.9357 14.4993 10.9012 14.381C10.7786 13.9604 10.8667 13.444 11.1217 13.0879C11.1935 12.9877 11.2792 12.9019 11.4507 12.7305C11.9751 12.2061 12.2373 11.9439 12.4087 11.6802C13.0552 10.6859 13.0552 9.40398 12.4087 8.40965C12.2373 8.14598 11.9751 7.88378 11.4507 7.35937L11.1584 7.06706C10.3612 6.2699 9.96262 5.87132 9.53455 5.6548C8.68321 5.2242 7.67781 5.2242 6.82646 5.6548C6.39839 5.87132 5.99981 6.2699 5.20265 7.06706L4.9662 7.30351C4.17176 8.09795 3.77454 8.49517 3.47117 9.03521C3.13454 9.63447 2.8925 10.5652 2.89454 11.2525C2.89639 11.872 3.01654 12.2953 3.25685 13.142C4.54831 17.6921 6.98502 21.9856 10.567 25.5676C14.149 29.1495 18.4425 31.5863 22.9926 32.8777C23.8393 33.118 24.2626 33.2382 24.882 33.24C25.5694 33.2421 26.5001 33 27.0993 32.6634C27.6394 32.36 28.0366 31.9628 28.831 31.1684L29.0675 30.9319C29.8647 30.1348 30.2632 29.7362 30.4798 29.3081C30.9104 28.4568 30.9104 27.4514 30.4798 26.6C30.2632 26.1719 29.8647 25.7734 29.0675 24.9762L28.7752 24.6839C28.2508 24.1595 27.9886 23.8973 27.7249 23.7259C26.7306 23.0794 25.4487 23.0794 24.4544 23.7259C24.1907 23.8973 23.9285 24.1595 23.4041 24.6839C23.2326 24.8554 23.1469 24.9411 23.0467 25.0129C22.6905 25.2679 22.1741 25.3559 21.7536 25.2333C21.6353 25.1989 21.5418 25.154 21.3547 25.0641C19.1803 24.0202 17.1423 22.597 15.34 20.7946Z" stroke="#009B31" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="contact-phone-text-container">
                  <div className="contact-phone-title">{t('info.phone.title')}</div>
                  <div className="contact-phone-details">{t('info.phone.number')}</div>
                  <div className="contact-phone-note">{t('info.phone.note')}</div>
                </div>
              </div>

              <div className="contact-email-container">
                <div className="contact-email-icon-container">
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M32.2499 27L22.2857 18M13.7143 18L3.75005 27M3 10.5L15.2474 19.0732C16.2391 19.7674 16.735 20.1145 17.2744 20.249C17.7508 20.3677 18.2492 20.3677 18.7256 20.249C19.265 20.1145 19.7609 19.7674 20.7526 19.0732L33 10.5M10.2 30H25.8C28.3202 30 29.5804 30 30.543 29.5095C31.3897 29.0781 32.0781 28.3897 32.5095 27.543C33 26.5804 33 25.3202 33 22.8V13.2C33 10.6798 33 9.41965 32.5095 8.45704C32.0781 7.61031 31.3897 6.9219 30.543 6.49047C29.5804 6 28.3202 6 25.8 6H10.2C7.67976 6 6.41965 6 5.45704 6.49047C4.61031 6.9219 3.9219 7.61031 3.49047 8.45704C3 9.41965 3 10.6798 3 13.2V22.8C3 25.3202 3 26.5804 3.49047 27.543C3.9219 28.3897 4.61031 29.0781 5.45704 29.5095C6.41965 30 7.67976 30 10.2 30Z" stroke="#009B31" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="contact-email-text-container">
                  <div className="contact-email-title">{t('info.email.title')}</div>
                  <div className="contact-email-details">{t('info.email.address')}</div>
                </div>
              </div>
            </div>

            <img
              src={`${MEDIA_URL}/images/adventure-awaits-banner-friends-campervan.webp`}
              alt="Contact location map"
              className="contact-map-image"
            />

            <div className="contact-form-section">
              <form onSubmit={handleFormSubmit} className="contact-form-fields">
                <div className="contact-name-fields">
                  <div className="contact-input-field">
                    <div className="contact-input-wrapper">
                      <div className="contact-input-state-layer">
                        <div className="contact-input-content">
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="contact-input-text"
                            required
                          />
                          <div className="contact-label-container">
                            <div className="contact-label-text">{t('form.firstName')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="contact-input-field">
                    <div className="contact-input-wrapper">
                      <div className="contact-input-state-layer">
                        <div className="contact-input-content">
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="contact-input-text"
                            required
                          />
                          <div className="contact-label-container">
                            <div className="contact-label-text">{t('form.lastName')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="contact-email-phone-fields">
                  <div className="contact-input-field">
                    <div className="contact-input-wrapper">
                      <div className="contact-input-state-layer">
                        <div className="contact-input-content">
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="contact-input-text"
                            required
                          />
                          <div className="contact-label-container">
                            <div className="contact-label-text">{t('form.email')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="contact-input-field">
                    <div className="contact-input-wrapper">
                      <div className="contact-input-state-layer">
                        <div className="contact-input-content">
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="contact-input-text"
                          />
                          <div className="contact-label-container">
                            <div className="contact-label-text">{t('form.phone')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="contact-message-field">
                  <div className="contact-textarea-wrapper">
                    <div className="contact-textarea-state-layer">
                      <div className="contact-textarea-content">
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          className="contact-textarea-text"
                          rows="6"
                          required
                        />
                        <div className="contact-label-container">
                          <div className="contact-label-text">{t('form.message')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {status.error && (
                  <div className="form-error" style={{ color: "red", marginBottom: "15px", textAlign: "center" }}>
                    {t('form.error')}
                  </div>
                )}

                {status.submitted && (
                  <div className="form-success" style={{ color: "green", marginBottom: "15px", textAlign: "center" }}>
                    {t('form.success')}
                  </div>
                )}

                <button
                  type="submit"
                  className="contact-submit-button"
                  disabled={status.submitting}
                >
                  <div className="contact-submit-state-layer">
                    <div className="contact-submit-label">
                      {status.submitting ? t('form.submitting') : t('form.submit')}
                    </div>
                  </div>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <CheckoutFooter />
    </div>
  );
};

export default Contact;
